import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
} from 'react'
import type { CropRect } from '@/lib/cover-spec'

interface ImageCropperProps {
  src: string
  naturalWidth: number
  naturalHeight: number
  /** Width ÷ height of the crop frame. */
  aspect: number
  onChange: (crop: CropRect) => void
  disabled?: boolean
}

const MAX_ZOOM = 4

/**
 * A fixed-ratio crop frame: drag to move the image, scroll or use the slider
 * to zoom. The image always covers the frame, so there is never empty space
 * in the result. Arrow keys pan, +/- zoom.
 */
export function ImageCropper({
  src,
  naturalWidth,
  naturalHeight,
  aspect,
  onChange,
  disabled = false,
}: ImageCropperProps) {
  const frame = useRef<HTMLDivElement>(null)
  const drag = useRef<{ x: number; y: number; id: number } | null>(null)
  const [frameWidth, setFrameWidth] = useState(0)

  // The largest crop of the right ratio that fits inside the image (zoom 1).
  const baseWidth = Math.min(naturalWidth, naturalHeight * aspect)

  const [zoom, setZoom] = useState(1)
  const [center, setCenter] = useState({ x: naturalWidth / 2, y: naturalHeight / 2 })
  // Read by the wheel listener, which can fire several times between renders.
  const zoomRef = useRef(1)

  const cropWidth = baseWidth / zoom
  const cropHeight = cropWidth / aspect

  // Keep the crop inside the image.
  const clamp = useCallback(
    (point: { x: number; y: number }, z: number) => {
      const w = baseWidth / z
      const h = w / aspect
      return {
        x: Math.min(Math.max(point.x, w / 2), naturalWidth - w / 2),
        y: Math.min(Math.max(point.y, h / 2), naturalHeight - h / 2),
      }
    },
    [aspect, baseWidth, naturalHeight, naturalWidth],
  )

  useEffect(() => {
    onChange({
      x: center.x - cropWidth / 2,
      y: center.y - cropHeight / 2,
      width: cropWidth,
      height: cropHeight,
    })
  }, [center, cropWidth, cropHeight, onChange])

  useLayoutEffect(() => {
    const element = frame.current
    if (!element) return
    const observer = new ResizeObserver(([entry]) => setFrameWidth(entry.contentRect.width))
    observer.observe(element)
    setFrameWidth(element.getBoundingClientRect().width)
    return () => observer.disconnect()
  }, [])

  const applyZoom = useCallback(
    (next: number) => {
      const z = Math.min(Math.max(next, 1), MAX_ZOOM)
      zoomRef.current = z
      setZoom(z)
      setCenter((c) => clamp(c, z))
    },
    [clamp],
  )

  // React's onWheel is passive, so preventDefault needs a native listener.
  useEffect(() => {
    const element = frame.current
    if (!element || disabled) return
    const onWheel = (event: WheelEvent) => {
      event.preventDefault()
      applyZoom(zoomRef.current * Math.exp(-event.deltaY * 0.0015))
    }
    element.addEventListener('wheel', onWheel, { passive: false })
    return () => element.removeEventListener('wheel', onWheel)
  }, [applyZoom, disabled])

  // Screen pixels per image pixel.
  const scale = frameWidth > 0 ? frameWidth / cropWidth : 0

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (disabled) return
    event.currentTarget.setPointerCapture(event.pointerId)
    drag.current = { x: event.clientX, y: event.clientY, id: event.pointerId }
  }

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const start = drag.current
    if (!start || start.id !== event.pointerId || scale === 0) return
    const dx = (event.clientX - start.x) / scale
    const dy = (event.clientY - start.y) / scale
    drag.current = { ...start, x: event.clientX, y: event.clientY }
    setCenter((c) => clamp({ x: c.x - dx, y: c.y - dy }, zoom))
  }

  const endDrag = (event: PointerEvent<HTMLDivElement>) => {
    if (drag.current?.id === event.pointerId) drag.current = null
  }

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return
    const step = cropWidth * 0.04
    const moves: Record<string, [number, number]> = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
    }
    if (moves[event.key]) {
      event.preventDefault()
      const [dx, dy] = moves[event.key]
      setCenter((c) => clamp({ x: c.x + dx, y: c.y + dy }, zoom))
    } else if (event.key === '+' || event.key === '=') {
      event.preventDefault()
      applyZoom(zoom * 1.1)
    } else if (event.key === '-') {
      event.preventDefault()
      applyZoom(zoom / 1.1)
    }
  }

  return (
    <div className="image-cropper">
      <div
        ref={frame}
        className="image-cropper-frame"
        style={{ aspectRatio: String(aspect) }}
        tabIndex={disabled ? -1 : 0}
        role="application"
        aria-label="Crop area. Drag or use arrow keys to move, scroll or plus and minus to zoom."
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onKeyDown={onKeyDown}
      >
        {scale > 0 ? (
          <img
            src={src}
            alt=""
            draggable={false}
            className="image-cropper-image"
            style={{
              width: naturalWidth * scale,
              height: naturalHeight * scale,
              transform: `translate(${frameWidth / 2 - center.x * scale}px, ${
                frameWidth / aspect / 2 - center.y * scale
              }px)`,
            }}
          />
        ) : null}
        <div className="image-cropper-grid" aria-hidden="true" />
      </div>

      <label className="image-cropper-zoom">
        <span>Zoom</span>
        <input
          type="range"
          min={1}
          max={MAX_ZOOM}
          step={0.01}
          value={zoom}
          disabled={disabled}
          onChange={(event) => applyZoom(Number(event.target.value))}
        />
      </label>
    </div>
  )
}
